import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSession } from "../auth/session";
import { titleCase } from "../lib/format";
import { isTextEntryTarget } from "../lib/keyboard";
import { getCore, listCores, loadToolUI } from "../registry/toolRegistry";
import { jobTitle } from "../shell/JobsList";
import { useFolders, useJobs } from "../state/queries";
import { useUiStore } from "../state/uiStore";
import { Icon, cx } from "../ui";
import {
  buildIndex,
  buildSuggestions,
  currentToolIdFromPath,
  draftForPlan,
  type FolderEntry,
  type JobEntry,
  type Suggestion,
} from "./suggest";

// D123/D126 Globale Suche (§11) — Type-to-search: tippt der User, während KEIN Eingabefeld
// fokussiert ist, öffnet sich oben die Suchleiste („/"-Präfix) mit Suggestions; Enter
// öffnet den Treffer (Tool/Screen/Job/Folder) bzw. wendet Parameter aufs aktuelle Tool an.
// Die LOGIK (Index/Parser/Plan) lebt React-frei in suggest.ts — hier nur Trigger + Anzeige
// + Ausführung (setDraft VOR navigate, damit der SettingsPanel-Default-Seed den
// Palette-Draft nie überschreibt; D65-Mechanik). D126: Jobs (D98-Titel) und Folder kommen
// aus dem Query-Cache (dieselben Hooks wie JobsList/FolderTree — kein Extra-Fetch-Pfad).

export function SearchPalette() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [sel, setSel] = useState(0);
  // Echte Anzeige-Namen der lazy WebToolUIs (z.B. „Image Generation") — beim ersten Öffnen
  // einmalig nachgeladen (loadToolUI ist gecacht); bis dahin sucht der Index über titleCase(id).
  const [uiNames, setUiNames] = useState<ReadonlyMap<string, string> | undefined>(undefined);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Synchroner Offen-Spiegel: schnelles Tippen kann ein zweites keydown feuern, bevor nach
  // setOpen(true) Re-Render/Effekte gelaufen sind — der Ref-Guard verhindert, dass das zweite
  // Zeichen das erste überschreibt. Plus Fokus-Restore-Ziel fürs Schließen (Codex-P2).
  const openRef = useRef(false);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  // Globaler Trigger (CAPTURE-Phase, vor den Editor-/Studio-Hotkeys): druckbares Zeichen oder „/".
  // Auf Hotkey-Screens (Editor: D78/D102; D139-Studio: Space/K/←/→) öffnet NUR „/" — nackte
  // Buchstaben/Ziffern sind dort Hotkeys (sonst kollidierte z.B. „K"=Cut mit der Type-to-Search).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (openRef.current) return; // offen → das Palette-Input hat den Fokus und übernimmt
      if (e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;
      if (isTextEntryTarget(document.activeElement)) return;
      // Codex-P1: nicht ÜBER einem offenen Dialog öffnen (AuthModal teilt die Overlay-Ebene).
      if (useUiStore.getState().authOpen) return;
      const inHotkeyScreen = pathname.startsWith("/editor") || pathname.startsWith("/studio");
      const isSlash = e.key === "/";
      // jedes druckbare Zeichen außer Space (PRD §11; Sondertasten haben mehrstellige e.key-Namen)
      const isChar = e.key.length === 1 && e.key !== " ";
      if (!isSlash && (!isChar || inHotkeyScreen)) return;
      e.preventDefault();
      e.stopPropagation();
      openRef.current = true;
      restoreFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setQuery(isSlash ? "" : e.key);
      setSel(0);
      setOpen(true);
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [pathname]);

  useEffect(() => {
    if (!open || uiNames) return;
    let active = true;
    void Promise.all(
      listCores().map((c) =>
        loadToolUI(c.id)
          .then((ui) => [c.id, ui?.name] as const)
          .catch(() => [c.id, undefined] as const),
      ),
    ).then((pairs) => {
      if (active) setUiNames(new Map(pairs.filter((p): p is readonly [string, string] => typeof p[1] === "string")));
    });
    return () => {
      active = false;
    };
  }, [open, uiNames]);

  // D126: Jobs + Folder als Such-Ziele — aus dem Query-Cache (abgemeldet: leere Listen).
  // Jobs ohne registrierten Core (deprecated Tool, D32) sind nicht öffenbar → nicht im Index
  // (wie der deaktivierte JobsList-Klick); Titel = D98-Logik (jobTitle, Fallback Tool-Name).
  // D149/Codex-R2-P0: NUR bei aktiver Identität aus dem (persistierten) Query-Cache indexieren.
  // Sonst könnten beim Warm-Boot eines anderen Users (Cache hydriert, Session noch `loading`, vor
  // dem Owner-Purge) fremde Job-Titel/Ordnernamen in der Suche auftauchen.
  const { status } = useSession();
  const signedIn = status === "signed-in";
  const jobsQ = useJobs();
  const foldersQ = useFolders();
  const dynamic = useMemo(() => {
    if (!signedIn) return { folders: [], jobs: [] };
    const jobs: JobEntry[] = (jobsQ.data ?? []).flatMap((j) => {
      const core = getCore(j.tool_id);
      if (!core) return [];
      return [
        {
          type: "job" as const,
          id: j.id,
          names: [jobTitle(j) ?? titleCase(j.tool_id)],
          path: `/generate/${core.category}/${core.id}`,
          icon: "clock",
          detail: `Job · ${titleCase(j.tool_id)} · ${j.status}`,
        },
      ];
    });
    const folders: FolderEntry[] = [
      ...(foldersQ.data ?? [])
        .filter((f) => f.trashed_at == null) // Soft-Trash (D20) ist kein Such-Ziel
        .map((f): FolderEntry => ({ type: "folder", id: f.id, names: [f.name], icon: "folder" })),
      // Inbox = virtueller folder_id-IS-NULL-View (§11/D20) — als benanntes Ziel suchbar.
      { type: "folder", id: null, names: ["Inbox"], icon: "inbox" },
    ];
    return { folders, jobs };
  }, [signedIn, jobsQ.data, foldersQ.data]);

  const currentToolId = useMemo(() => currentToolIdFromPath(pathname), [pathname]);
  // Codex-P2 (D126-R1): Bestands-Draft des aktuellen Tools → Hidden-Filter der apply-
  // Suggestions gegen das EFFEKTIVE Modell (sonst zeigt die Vorschau Patches, die
  // draftForPlan/Submit später strippen).
  const currentDraft = useUiStore((s) => (currentToolId ? s.drafts[currentToolId] : undefined));
  const index = useMemo(() => buildIndex(uiNames, dynamic), [uiNames, dynamic]);
  const suggestions = useMemo(
    () => (open ? buildSuggestions(query, index, currentToolId, currentDraft) : []),
    [open, query, index, currentToolId, currentDraft],
  );
  const selIdx = Math.min(sel, Math.max(0, suggestions.length - 1));

  function close() {
    openRef.current = false;
    setOpen(false);
    setQuery("");
    setSel(0);
    // Codex-P2: Fokus dahin zurück, wo er vor dem Öffnen war (nach Navigation ggf.
    // unmounted → focus() ist dann ein No-op, der Browser fokussiert den Body).
    restoreFocusRef.current?.focus();
    restoreFocusRef.current = null;
  }

  function run(s: Suggestion) {
    const { plan } = s;
    // D138: Query-Vervollständigung (Zahl-Feld ohne Wert) — die Palette bleibt OFFEN, der
    // User tippt den Wert; Fokus zurück ins Input (ein Maus-Klick fokussierte den Button).
    if (plan.completeQuery !== undefined) {
      setQuery(plan.completeQuery);
      setSel(0);
      inputRef.current?.focus();
      return;
    }
    const store = useUiStore.getState();
    if (plan.toolId) {
      const core = getCore(plan.toolId);
      if (core) {
        // Frischer Compose beim Tool-ÖFFNEN: eine noch ausstehende D65-Job-Rehydrierung
        // (useOpenedJob lädt asynchron) darf den Palette-Draft nicht überschreiben.
        if (plan.freshCompose) store.closeJob();
        store.setDraft(plan.toolId, draftForPlan(core, store.getDraft(plan.toolId), plan));
      }
    }
    // D126: Job öffnen (D65 — Rehydrierung macht useOpenedJob) bzw. Ordner auswählen.
    if (plan.openJobId) store.openJob(plan.openJobId);
    if (plan.selectFolder) store.selectFolder(plan.selectFolder.id);
    if (plan.navigateTo) navigate(plan.navigateTo);
    close();
  }

  function onInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSel(suggestions.length ? (selIdx + 1) % suggestions.length : 0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSel(suggestions.length ? (selIdx - 1 + suggestions.length) % suggestions.length : 0);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const s = suggestions[selIdx];
      if (s) run(s);
    } else if (e.key === "Escape" || (e.key === "Backspace" && query === "")) {
      e.preventDefault();
      close();
    } else if (e.key === "Tab") {
      // Codex-P2: Fokus-Trap — Tab wandert nicht in die UI darunter (Navigation per Pfeiltasten).
      e.preventDefault();
    }
  }

  if (!open) return null;

  return (
    // z-[60]: deterministisch ÜBER AuthModal/Drawern (z-50/z-40) — der Trigger öffnet zwar
    // nicht bei offenem Auth-Dialog (Codex-P1), aber die Ebene bleibt so eindeutig.
    <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Suche">
      {/* transparenter Click-Catcher — Klick daneben schließt */}
      <div className="absolute inset-0" onClick={close} />
      <div className="absolute inset-x-0 top-6 mx-auto w-[min(640px,calc(100vw-2rem))]">
        <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-panel">
          <div className="flex items-center gap-2 border-b border-border px-3">
            <Icon name="search" size={18} className="shrink-0 text-muted" />
            <span aria-hidden="true" className="select-none text-base font-medium text-muted">
              /
            </span>
            <input
              ref={inputRef}
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSel(0);
              }}
              onKeyDown={onInputKeyDown}
              placeholder="Tool, Screen oder Parameter — z.B. „Spritesheet Loco Shift 5s“"
              aria-label="Suchbegriff"
              role="combobox"
              aria-expanded={suggestions.length > 0}
              aria-controls="search-suggestions"
              aria-activedescendant={suggestions[selIdx] ? `search-option-${suggestions[selIdx].id}` : undefined}
              className="h-12 w-full bg-transparent text-sm text-text outline-none placeholder:text-muted/60"
            />
          </div>
          <ul id="search-suggestions" role="listbox" aria-label="Vorschläge" className="max-h-[320px] overflow-y-auto p-1">
            {suggestions.map((s, i) => (
              <li key={s.id} id={`search-option-${s.id}`} role="option" aria-selected={i === selIdx}>
                <button
                  type="button"
                  onMouseEnter={() => setSel(i)}
                  onClick={() => run(s)}
                  className={cx(
                    "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm",
                    i === selIdx ? "bg-accent/15 text-accent" : "text-text hover:bg-surface-2",
                  )}
                >
                  <Icon name={s.icon} size={16} className="shrink-0" />
                  <span className="shrink-0 font-medium">{s.title}</span>
                  {s.detail && <span className="truncate text-xs text-muted">{s.detail}</span>}
                </button>
              </li>
            ))}
            {suggestions.length === 0 && <li className="px-3 py-2 text-sm text-muted">Keine Treffer</li>}
          </ul>
        </div>
      </div>
    </div>
  );
}
